import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, CheckCircle, AlertCircle, Home } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { generateAndDownloadPDF } from '../utils/pdfGenerator';

export default function PaymentSuccessPage() {
  const [paymentData, setPaymentData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  
  useEffect(() => {
    // Retrieve payment data from localStorage
    const savedData = localStorage.getItem('pdf_facile_payment');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        // Check if data is not too old (24 hours)
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          setPaymentData(data);
        }
      } catch (e) {
        console.error('Error parsing payment data', e);
      }
    }
  }, []);
  
  const handleDownload = () => {
    if (!paymentData) return;
    
    setDownloading(true);
    
    try {
      // Use the new comprehensive PDF generator
      generateAndDownloadPDF(
        paymentData.documentId,
        paymentData.documentName,
        paymentData.formData
      );
      
      // Clear localStorage after successful download
      localStorage.removeItem('pdf_facile_payment');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
    
    setDownloading(false);
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 flex items-center justify-center">
        <div className="container-app max-w-md text-center py-12">
          <div data-testid="payment-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold mb-4 text-green-700">
              Paiement réussi !
            </h1>
            
            {paymentData ? (
              <>
                <p className="text-muted-foreground mb-2">
                  Votre document <strong>{paymentData.documentName}</strong> est prêt.
                </p>
                <p className="text-muted-foreground mb-8">
                  Cliquez sur le bouton ci-dessous pour télécharger votre PDF complet.
                </p>
                
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="btn-primary w-full mb-4"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {downloading ? 'Génération...' : 'Télécharger le PDF'}
                </Button>
                
                <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-slate-600">
                    <strong>Votre PDF inclut :</strong>
                  </p>
                  <ul className="text-sm text-slate-500 mt-2 space-y-1">
                    <li>✓ Document complet et structuré</li>
                    <li>✓ Toutes vos informations intégrées</li>
                    <li>✓ Format compatible enveloppe à fenêtre</li>
                    <li>✓ Prêt à imprimer et envoyer</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-8">
                  Merci pour votre achat ! Si le téléchargement ne démarre pas automatiquement, 
                  veuillez retourner sur la page du document et réessayer.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-800 text-sm text-left">
                      Les données du document n'ont pas été trouvées. Cela peut arriver si vous avez 
                      effacé le cache de votre navigateur ou si plus de 24h se sont écoulées.
                    </p>
                  </div>
                </div>
              </>
            )}
            
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-accent hover:underline"
            >
              <Home className="w-4 h-4" />
              Créer un autre document
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
